import { apiUrl, otherProviderValue, providersConfig, validationMessagesConfig } from '../config';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { MenuItem } from '@chakra-ui/react';
import axios from 'axios';
import { validateForm } from '../helpers';

type STEP_NAME = 'SUBSCRIBE' | 'SUBSCRIBE_SUCCESS' | 'SUBSCRIBE_ERROR';

export const useSubscription = (setNextStep?: (step: STEP_NAME) => void) => {
  const [email, setEmail] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [showTextProviderInput, setShowTextProviderInput] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState({ value: '', label: '' });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPolicyAgreed, setIsPolicyAgreed] = useState(false);
  const [policyError, setPolicyError] = useState<string | null>(null);
  const [providerError, setProviderError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [availableOptions, setAvailableOptions] = useState([
    ...providersConfig,
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailChange = (e: React.FormEvent<HTMLInputElement>) => {
    setEmail(e.currentTarget.value);
    setEmailError(null);
  };

  const handleManualProviderChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSelectedProvider({ label: e.currentTarget.value, value: e.currentTarget.value });
    setProviderError(e.currentTarget.value.length ? null : validationMessagesConfig.provider);
  };

  const toggleManualProviderInput = () => {
    setSelectedProvider({ label: '', value: '' });
    setShowTextProviderInput(!showTextProviderInput);
  };

  const handleSearchInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const searchStr = e.currentTarget.value;
    setSearchValue(searchStr);
    const newAvailableOptions = providersConfig.filter((option) =>
      option.label.toLowerCase().includes(searchStr.toLowerCase())
    );
    setAvailableOptions(newAvailableOptions);
  };

  const handleMenuItemClick = (e: React.FormEvent<HTMLButtonElement>) => {
    const itemValue = e.currentTarget.value;

    if (itemValue === otherProviderValue) {
      setSelectedProvider({ value: '',label: '' });
      setShowTextProviderInput(true);
      setProviderError(null);
      return;
    }

    setShowTextProviderInput(false);

    const providerOption = providersConfig.filter(
      (provider) => provider.value === itemValue
    );
    setSelectedProvider(providerOption[0]);
    setProviderError(null);
  };

  const handlePolicyChange = () => {
    setIsPolicyAgreed(!isPolicyAgreed);
    setPolicyError(null);
  };

  const sendRequest = useCallback(
    (params: { email: string; isp: string; privacyTerms: boolean }) => {
      axios
        .post(`${apiUrl}/api/subscriptions`, { ...params })
        .then(() => {
          setIsLoading(false);
          if (setNextStep) {
            setNextStep('SUBSCRIBE_SUCCESS');
          }
        })
        .catch((error) => {
          setIsLoading(false);
          if (setNextStep) {
            setNextStep('SUBSCRIBE_ERROR');
          }
          console.log('Something wrong: ', error);
        });
    },
    [email, selectedProvider]
  );

  useEffect(() => {
    if (emailError || policyError || providerError) {
      setIsFormValid(false);
    }
  }, [emailError, policyError, providerError]);

  const subscribe = () => {
    const errors = validateForm({ email, selectedProvider, isPolicyAgreed });
    if (Object.keys(errors).length > 0) {
      const { emailError, providerError, policyError } = errors;
      setEmailError(emailError || null);
      setPolicyError(policyError || null);
      setProviderError(providerError || null);
    }
    if (Object.keys(errors).length === 0) {
      setIsLoading(true);
      sendRequest({
        email,
        isp: selectedProvider.value,
        privacyTerms: isPolicyAgreed,
      });
    }
  };

  const getNewMenuOptions = useMemo(() => {
    const list = availableOptions.map((option) => {
      return (
        <MenuItem
          className='!text-[#475467]'
          key={option.value}
          value={option.value}
          onClick={handleMenuItemClick}
        >
          {option.label}
        </MenuItem>
      );
    });
    return <>{list}</>;
  }, [availableOptions]);

  return {
    email,
    searchValue,
    selectedProvider,
    showTextProviderInput,
    isFormValid,
    isPolicyAgreed,
    policyError,
    providerError,
    emailError,
    availableOptions,
    isLoading,
    handleEmailChange,
    handleManualProviderChange,
    toggleManualProviderInput,
    handleSearchInputChange,
    handleMenuItemClick,
    handlePolicyChange,
    subscribe,
    getNewMenuOptions,
  };
};
